import type * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import type { GraphicsRequirements } from '@/graphics/GraphicsRequirements'
import type { PhaseRenderContext } from '@/shared/types/assets'

/** ControlPlugin 与其私有 overlay contributor 共用的双后端要求。 */
export const CONTROL_GRAPHICS_REQUIREMENTS: GraphicsRequirements = Object.freeze({
  backends: Object.freeze(['webgl2', 'webgpu'] as const),
  capabilities: Object.freeze(['surfaceRender', 'multiView'] as const),
})

/**
 * ControlPlugin 私有的 Scene overlay 提交适配器。
 *
 * 它只服务 Earth/SpaceMouse 的默认 surface 覆盖层，不向宿主开放通用 Scene、offscreen target 或
 * readback API。提交统一走 `GraphicsBackend.renderScene` 契约（见 GraphicsBackend 的契约注释），
 * 因此不再自行改写 viewport/scissor/render target，也不会求值 PhaseRenderContext 的 legacy
 * renderer/outputTarget getter。
 */
export class ControlOverlayGraphicsAdapter {
  private readonly stateOwner = Object.freeze({ kind: 'control-overlay-state-owner' })

  render(scene: THREE.Scene, camera: THREE.Camera, context: PhaseRenderContext): void {
    const { graphics } = context
    const renderScene = graphics.renderScene
    if (typeof renderScene !== 'function') {
      throw new UnsupportedGraphicsCapabilityError(
        'control-overlay-surface-render',
        graphics.kind,
        '要求 GraphicsBackend.renderScene 受控 surface 提交原语'
      )
    }

    const state = graphics.captureState(this.stateOwner)
    const savedCameraLayerMask = camera.layers.mask
    // 领域语义：覆盖层相机必须看到全部 layer，渲染后恢复原掩码。
    camera.layers.mask = 0xffffffff

    try {
      // viewport/scissor/outputTarget 由 backend 依当前 View 决定，这里不再重复包装。
      renderScene.call(graphics, scene, camera)
    } finally {
      camera.layers.mask = savedCameraLayerMask
      graphics.restoreState(state, this.stateOwner)
    }
  }
}

/** ControlPlugin 私有的共享 overlay 提交实例。 */
export const controlOverlayGraphicsAdapter = new ControlOverlayGraphicsAdapter()
